import React, { useState, useEffect } from 'react';
import './App.css';
import DewormingCalculator from './DewormingCalculator';
import HorseEditor from './HorseEditor';
import WeightEditor from './WeightEditor';
import ContractEditor from './ContractEditor';
import PhoneList from './PhoneList';
import ContactEditor from './ContactEditor';
import PayrollView from './PayrollView';
import EmployeeEditor from './EmployeeEditor';
import BoxPlanView from './BoxPlanView';
import PriceAdjustment from './PriceAdjustment';
import ContractTemplateEditor from './ContractTemplateEditor';
import ContractDocumentsView from './ContractDocumentsView';
import Dashboard from './Dashboard';
import { CompanySettings } from './types';
import { fetchCompanySettings } from './api';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import UpdateNotification from './UpdateNotification';
import PasswordResetConfirm from './PasswordResetConfirm';
import WorkerShell from './WorkerShell';

type View = 'dashboard' | 'deworming' | 'editor' | 'weights' | 'contract' | 'contract-template' | 'contract-documents' | 'phonelist' | 'contacts' | 'payroll' | 'employees' | 'boxplan' | 'prices' | 'impressum' | 'contactinfo' | 'students';

function AppContent() {
  const [currentView, setCurrentView] = useState<View>('dashboard');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [hashRoute, setHashRoute] = useState(window.location.hash);
  const [resetSuccessMessage, setResetSuccessMessage] = useState('');
  const [company, setCompany] = useState<CompanySettings>({});
  const { isAuthenticated, isLoading, login, logout, user } = useAuth();

  // Load company settings once authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCompanySettings()
        .then(setCompany)
        .catch(err => console.error('Failed to load company settings:', err));
    }
  }, [isAuthenticated]);

  const c = (key: string) => company[key]?.value || '';

  // Listen for service worker updates
  useEffect(() => {
    const handleUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<ServiceWorkerRegistration>;
      const registration = customEvent.detail;
      if (registration && registration.waiting) {
        setWaitingWorker(registration.waiting);
        setShowUpdateNotification(true);
      }
    };

    window.addEventListener('swUpdate', handleUpdate);
    return () => window.removeEventListener('swUpdate', handleUpdate);
  }, []);

  const handleUpdate = () => {
    if (waitingWorker) {
      // Set up listener for controller change (when new SW takes over)
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });

      // Tell the waiting service worker to skip waiting and activate immediately
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });

      // Fallback: if controllerchange doesn't fire within 2s, reload anyway
      setTimeout(() => {
        if (!refreshing) {
          window.location.reload();
        }
      }, 2000);
    } else {
      // No waiting worker, just reload
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setShowUpdateNotification(false);
  };

  const handleNavClick = (view: View) => {
    setCurrentView(view);
    setMenuOpen(false); // Close menu after selection on mobile
  };

  // Listen for hash changes
  useEffect(() => {
    const handleHashChange = () => {
      setHashRoute(window.location.hash);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Handle password reset route (accessible without authentication)
  if (hashRoute.startsWith('#/reset-password')) {
    const params = new URLSearchParams(hashRoute.split('?')[1] || '');
    const token = params.get('token');

    if (!token) {
      window.location.hash = '#/login';
      return <div className="App">Ungültiger Link</div>;
    }

    return (
      <PasswordResetConfirm
        token={token}
        onSuccess={() => {
          setResetSuccessMessage('Passwort erfolgreich zurückgesetzt! Sie können sich jetzt anmelden.');
          window.location.hash = '#/login';
        }}
      />
    );
  }

  if (isLoading) {
    return <div className="App">Laden...</div>;
  }

  if (!isAuthenticated) {
    return <Login onLogin={login} successMessage={resetSuccessMessage} />;
  }

  // Workers see only the time tracking interface
  if (user?.role === 'worker') {
    return <WorkerShell />;
  }

  return (
    <div className="App">
      {showUpdateNotification && (
        <UpdateNotification onUpdate={handleUpdate} onDismiss={handleDismiss} />
      )}
      <nav className="main-nav">
        <div className="nav-header">
          <div
            className="nav-title clickable"
            onClick={() => handleNavClick('dashboard')}
            style={{ cursor: 'pointer' }}
          >
            <h1>Reitstall Manager</h1>
            <span className="app-version">v{process.env.REACT_APP_VERSION || '2.0.0'}</span>
          </div>
          <div className="nav-actions">
            {currentView !== 'dashboard' && (
              <button
                className="btn-home"
                onClick={() => handleNavClick('dashboard')}
                title="Zurück zum Dashboard"
              >
                🏠 Home
              </button>
            )}
            <button className="btn-logout" onClick={logout}>
              Abmelden ({user?.username})
            </button>
          </div>
        </div>
        <div className={`nav-buttons ${menuOpen ? 'open' : ''}`} style={{ display: 'none' }}>
          <button
            className={currentView === 'deworming' ? 'active' : ''}
            onClick={() => handleNavClick('deworming')}
          >
            Wurmkur Berechnung
          </button>
          <button
            className={currentView === 'weights' ? 'active' : ''}
            onClick={() => handleNavClick('weights')}
          >
            Gewichts-Editor
          </button>
          <button
            className={currentView === 'contract' ? 'active' : ''}
            onClick={() => handleNavClick('contract')}
          >
            Einstellervertrag
          </button>
          <button
            className={currentView === 'editor' ? 'active' : ''}
            onClick={() => handleNavClick('editor')}
          >
            Pferde Editor
          </button>
          <button
            className={currentView === 'phonelist' ? 'active' : ''}
            onClick={() => handleNavClick('phonelist')}
          >
            Telefon Liste
          </button>
          <button
            className={currentView === 'contacts' ? 'active' : ''}
            onClick={() => handleNavClick('contacts')}
          >
            Kontakt Editor
          </button>
          <button
            className={currentView === 'payroll' ? 'active' : ''}
            onClick={() => handleNavClick('payroll')}
          >
            Stundenabrechnung
          </button>
          <button
            className={currentView === 'employees' ? 'active' : ''}
            onClick={() => handleNavClick('employees')}
          >
            Mitarbeiter
          </button>
          <button
            className={currentView === 'impressum' ? 'active' : ''}
            onClick={() => handleNavClick('impressum')}
          >
            Impressum
          </button>
          <button
            className={currentView === 'contactinfo' ? 'active' : ''}
            onClick={() => handleNavClick('contactinfo')}
          >
            Kontakt
          </button>
          <button className="logout-button" onClick={logout}>
            Abmelden ({user?.username})
          </button>
        </div>
      </nav>

      {currentView === 'dashboard' && <Dashboard onNavigate={(view: string) => setCurrentView(view as View)} />}
      {currentView === 'deworming' && <DewormingCalculator />}
      {currentView === 'weights' && <WeightEditor />}
      {currentView === 'contract' && <ContractEditor />}
      {currentView === 'editor' && <HorseEditor />}
      {currentView === 'phonelist' && <PhoneList />}
      {currentView === 'contacts' && <ContactEditor />}
      {currentView === 'payroll' && <PayrollView onNavigate={(view: string) => setCurrentView(view as View)} />}
      {currentView === 'employees' && <EmployeeEditor />}
      {currentView === 'boxplan' && <BoxPlanView onNavigate={(view: string) => setCurrentView(view as View)} />}
      {currentView === 'prices' && <PriceAdjustment />}
      {currentView === 'contract-template' && <ContractTemplateEditor />}
      {currentView === 'contract-documents' && <ContractDocumentsView />}
      {currentView === 'students' && (
        <div className="content-container">
          <h2>🎓 Reitschüler-Verwaltung</h2>
          <div className="content-card">
            <p><strong>Diese Funktion wird in Kürze verfügbar sein!</strong></p>
            <p>Hier können Sie dann folgende Funktionen nutzen:</p>
            <ul style={{ lineHeight: '1.8', marginTop: '20px' }}>
              <li>📋 Reitschüler verwalten (Name, Kontaktdaten, Erfahrungslevel)</li>
              <li>📅 Reitstunden planen und buchen</li>
              <li>🐴 Pferde-Zuordnung für Unterricht</li>
              <li>💰 Unterrichtsgebühren verwalten</li>
              <li>📊 Übersicht über gebuchte Stunden</li>
              <li>📝 Notizen und Fortschritte dokumentieren</li>
            </ul>
            <p style={{ marginTop: '30px', fontStyle: 'italic', color: '#666' }}>
              Wir arbeiten an dieser Funktion. Vielen Dank für Ihre Geduld!
            </p>
          </div>
        </div>
      )}
      {currentView === 'impressum' && (
        <div className="content-container">
          <h2>Impressum</h2>
          <div className="content-card">
            <p><strong>Angaben gemäß § 5 TMG:</strong></p>
            <p>
              {c('company_name')}<br />
              Geschäftsführer: {c('managing_directors')}<br />
              {c('street')}<br />
              {c('city')}
            </p>
            <p><strong>Kontakt:</strong></p>
            <p>
              Telefon: {c('phone')}<br />
              Mobil: {c('mobile')}<br />
              E-Mail: {c('email')}<br />
              Web: {c('website')}
            </p>
            <p><strong>Bankverbindung:</strong></p>
            <p>
              {c('bank_name')}<br />
              Kontoinhaber: {c('account_holder')}<br />
              IBAN: {c('iban')}<br />
              BIC: {c('bic')}
            </p>
            <p><strong>Steuerdaten:</strong></p>
            <p>
              {c('tax_office')}<br />
              Stnr.: {c('tax_number')}
            </p>
            <p><strong>Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:</strong></p>
            <p>
              {c('managing_directors')}<br />
              {c('street')}, {c('city')}
            </p>
          </div>
        </div>
      )}
      {currentView === 'contactinfo' && (
        <div className="content-container">
          <h2>Kontakt</h2>
          <div className="content-card">
            <p><strong>Haben Sie Fragen oder Anregungen?</strong></p>
            <p>Wir freuen uns über Ihre Nachricht!</p>
            <p>
              <strong>Adresse:</strong><br />
              {c('company_name')}<br />
              {c('street')}<br />
              {c('city')}
            </p>
            <p>
              <strong>Telefon:</strong> {c('phone')}<br />
              <strong>Mobil:</strong> {c('mobile')}<br />
              <strong>E-Mail:</strong> {c('email')}
            </p>
            <p>
              <strong>Erreichbarkeit:</strong><br />
              {c('contact_hours')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
